/**
 * PointsPill — clickable points indicator in the top-bar with a Duolingo-style
 * floating "+X pts" animation that rises and fades whenever the value goes up.
 *
 * - Polls /me/stats every 10s (so a delta is detected within 10s of any backend
 *   point award).
 * - Listens to a global `shariti:points-changed` CustomEvent so pages doing a
 *   user-triggered action can fire an immediate refresh (no polling delay).
 * - When a positive delta is detected and the pill is OUT of viewport (the user
 *   was scrolled down — e.g. after submitting a quiz at the bottom of the page),
 *   we **smoothly scroll to top first** then wait ~800ms before showing the
 *   floating "+X pts" so the user actually sees the animation.
 * - Persists the last-known points to sessionStorage (keyed by user_id) so
 *   navigating between pages (which remounts Layout) does NOT replay the
 *   animation against a stale AuthContext value (iter-26.5 fix).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

let _floatId = 0;
const STORAGE_PREFIX = "shariti:last-points:";

export default function PointsPill({ initialPoints = 0 }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const userId = user?.user_id || "anon";
    const storageKey = STORAGE_PREFIX + userId;

    // Baseline = max(sessionStorage value, initialPoints prop). This survives
    // page-to-page navigation (Layout remounts) without replaying animations
    // against a stale AuthContext.
    const baseline = useMemo(() => {
        if (typeof window === "undefined") return initialPoints;
        const stored = parseInt(
            window.sessionStorage.getItem(storageKey) || "",
            10,
        );
        if (Number.isFinite(stored)) {
            // Trust whichever is higher — if the stored value is higher,
            // AuthContext is stale (user gained points then navigated). If
            // initialPoints is higher (e.g. new login OR cross-tab gain), trust
            // it instead.
            return Math.max(stored, initialPoints);
        }
        return initialPoints;
    }, [storageKey, initialPoints]);

    const [points, setPoints] = useState(baseline);
    const [floats, setFloats] = useState([]); // [{id, delta}]
    const prevRef = useRef(baseline);
    const buttonRef = useRef(null);

    // Sync when prop changes (e.g. on login fresh user load) — but only if the
    // new initialPoints is greater than what we know (avoids stale-overwrite).
    useEffect(() => {
        if (initialPoints > prevRef.current) {
            prevRef.current = initialPoints;
            setPoints(initialPoints);
            try {
                window.sessionStorage.setItem(storageKey, String(initialPoints));
            } catch (e) {
                /* noop */
            }
        }
    }, [initialPoints, storageKey]);

    const persist = useCallback(
        (val) => {
            try {
                window.sessionStorage.setItem(storageKey, String(val));
            } catch (e) {
                /* noop */
            }
        },
        [storageKey],
    );

    const showFloat = useCallback((delta) => {
        const id = ++_floatId;
        setFloats((arr) => [...arr, { id, delta }]);
        // Auto-cleanup AFTER the animation completes (~2s)
        setTimeout(() => {
            setFloats((arr) => arr.filter((f) => f.id !== id));
        }, 2200);
    }, []);

    const handleDelta = useCallback(
        (delta) => {
            if (delta <= 0) return;
            // Check if the pill is currently visible. If not, scroll to top
            // first so the user actually witnesses the animation.
            const rect = buttonRef.current?.getBoundingClientRect();
            const inView =
                rect &&
                rect.top >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
            if (inView) {
                showFloat(delta);
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
                // Wait for the smooth scroll to settle (~600ms) + a small beat
                // so the user's eye is anchored on the pill before "+X pts" pops.
                setTimeout(() => showFloat(delta), 800);
            }
        },
        [showFloat],
    );

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/me/stats");
            const fresh = r.data?.points ?? prevRef.current;
            const delta = fresh - prevRef.current;
            if (delta > 0) handleDelta(delta);
            prevRef.current = fresh;
            setPoints(fresh);
            persist(fresh);
        } catch (e) {
            // silent
        }
    }, [handleDelta, persist]);

    // Polling loop (every 10s)
    useEffect(() => {
        const interval = setInterval(refresh, 10_000);
        return () => clearInterval(interval);
    }, [refresh]);

    // Listen for instant ping from pages (quiz submit, simulator end, etc.)
    useEffect(() => {
        const onPing = () => refresh();
        window.addEventListener("shariti:points-changed", onPing);
        return () => window.removeEventListener("shariti:points-changed", onPing);
    }, [refresh]);

    return (
        <div className="relative">
            <motion.button
                ref={buttonRef}
                onClick={() => navigate("/historique")}
                data-testid="topbar-points-button"
                title="Voir l'historique de mes points"
                aria-label={`${points} points — voir l'historique`}
                animate={
                    floats.length > 0
                        ? {
                              scale: [1, 1.12, 1],
                              boxShadow: [
                                  "0 0 0 0 rgba(238,114,46,0)",
                                  "0 0 0 12px rgba(238,114,46,0.35)",
                                  "0 0 0 0 rgba(238,114,46,0)",
                              ],
                          }
                        : {}
                }
                transition={{ duration: 0.9 }}
                className="flex items-center gap-1 sm:gap-2 glass-card rounded-2xl px-2 sm:pl-2 sm:pr-3 py-2 hover:border-shariti-orange/40 hover:bg-white/[0.07] transition-all group"
            >
                <Flame className="w-4 h-4 text-shariti-orange shrink-0" />
                <span
                    className="text-sm font-bold text-white tabular-nums"
                    data-testid="topbar-points"
                >
                    {points}
                </span>
                <span className="hidden sm:inline text-sm font-bold text-white">
                    pts
                </span>
            </motion.button>

            {/* Floating +X pts animation — rises ~80px, lingers, then fades */}
            <AnimatePresence>
                {floats.map((f) => (
                    <motion.div
                        key={f.id}
                        initial={{ opacity: 0, y: 0, scale: 0.8 }}
                        animate={{
                            opacity: [0, 1, 1, 1, 0],
                            y: [0, -28, -52, -72, -88],
                            scale: [0.8, 1.05, 1, 1, 0.95],
                        }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 2,
                            ease: [0.2, 0.8, 0.3, 1],
                            times: [0, 0.18, 0.5, 0.8, 1],
                        }}
                        className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 z-50"
                        data-testid="points-float"
                    >
                        <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-br from-shariti-yellow to-shariti-orange shadow-[0_4px_28px_rgba(238,114,46,0.65)] whitespace-nowrap">
                            <TrendingUp className="w-3.5 h-3.5 text-shariti-black" />
                            <span className="text-sm font-extrabold text-shariti-black tabular-nums">
                                +{f.delta} pts
                            </span>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}
